import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Container from '../../../Prototype/Container';
import Point from '../../../Utility/Point';
import RewardBoostersEffect from '../../../Prototype/Effect/RewardBoosterVFX';
import Sprite from '../../../Prototype/Sprite';
import TransformComponent from '../../../Component/Transform';
import { Easing, Tween } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import RewardBoostersNodeEffect from '../../../Prototype/Effect/RewardBoostersNodeEffect';
import { AwaitIterators } from '../AnimationHelper';
import ScoreCardEffect from '../../../Prototype/Effect/ScoreCardEffect';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';

interface IRewardBoostersData {
    type: 'tnt' | 'bullseye' | 'shuffle' | 'joker';
    amount: number;
    node: GameObject;
}

export class RewardBoosters extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: IRewardBoostersData
    ): IterableIterator<number> {
        const boosterData = {
            tnt: { name: 'TNT', texture: 'tnt_booster' },
            bullseye: { name: 'Bullseye', texture: 'bullseye_booster' },
            shuffle: { name: 'Shuffle', texture: 'shuffle_booster' },
            joker: { name: 'Joker', texture: 'Joker_Boost' },
        };

        animator.scene.emit(RenderSystemEvent.BRING_TO_FRONT, animator);

        const layoutContainer: Container = GameObject.Instantiate(Container, {
            name: 'layoutContainer',
            relativePosition: new Point(0.5, 0.5),
        });
        animator.addChild(layoutContainer);

        // Show currency reward effect and destroy it after complete.
        const rewardEffect: GameObject = GameObject.Instantiate(
            RewardBoostersEffect
        );
        layoutContainer.addChild(rewardEffect);
        rewardEffect.on(DragonbonesAnimationEvent.COMPLETE, () => {
            rewardEffect.destroy();
        });

        // Create and show the booster sprite.
        const booster: GameObject = GameObject.Instantiate(Sprite, {
            anchor: new Point(0.5, 0.5),
            scale: new Point(0.25, 0.25),
            texture: boosterData[meta.type].texture,
        });
        layoutContainer.addChild(booster);
        yield* this.showBooster(booster, 500);

        // Create an empty container to get the node position.
        const fakeNode: GameObject = GameObject.Instantiate(Container);
        meta.node.addChild(fakeNode);
        layoutContainer.addChild(fakeNode);
        const nodeTransform = fakeNode.getComponent(TransformComponent);

        // Mode booster to the node position.
        yield* this.moveBoosterToLevelNode(booster, nodeTransform, 500);

        // Show node effect and destroy it after complete.
        const nodeEffect: GameObject = GameObject.Instantiate(
            RewardBoostersNodeEffect,
            {
                position: new Point(nodeTransform.x - 5, nodeTransform.y - 10),
            }
        );
        layoutContainer.addChild(nodeEffect);
        nodeEffect.on(DragonbonesAnimationEvent.COMPLETE, () => {
            nodeEffect.destroy();
        });

        yield* delay(300);

        // Pop in a text from the node with the booster name.
        const boosterName: string = boosterData[meta.type].name;
        layoutContainer.addChild(
            GameObject.Instantiate(ScoreCardEffect, {
                text: `+${meta.amount} ${boosterName}`,
                position: nodeTransform.position.clone(),
            })
        );

        yield* delay(1000);

        layoutContainer.destroy();
        fakeNode.destroy();
    }

    private *showBooster(booster: GameObject, duration: number) {
        const boosterTransform: TransformComponent = booster.getComponent(
            TransformComponent
        );
        const tweenScale: Tween = this.getTween(boosterTransform.scale);

        tweenScale.to({ x: 1.2, y: 1.2 }, duration).easing(Easing.Cubic.Out);

        yield* this.startTween(tweenScale);
    }

    private *moveBoosterToLevelNode(
        booster: GameObject,
        nodeTransform: TransformComponent,
        duration: number
    ) {
        const boosterTransform: TransformComponent = booster.getComponent(
            TransformComponent
        );

        const tweenPosition: Tween = this.getTween(boosterTransform.position);
        const tweenScale: Tween = this.getTween(boosterTransform.scale);

        tweenPosition
            .to({ x: nodeTransform.x, y: nodeTransform.y }, duration)
            .easing(Easing.Cubic.In);

        tweenScale
            .to({ x: 0.4, y: 0.4 }, duration)
            .easing(Easing.Cubic.In)
            .onComplete(() => booster.destroy());

        yield* AwaitIterators([
            this.startTween(tweenPosition),
            this.startTween(tweenScale),
        ]);
    }
}
